/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WorkflowBuilder from '../components/WorkflowBuilder';
import apiClient from '../api/client';
import { toast } from 'react-toastify';

const WORKFLOWS_ROUTE = '/workflows';

const WorkflowEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/workflows/${id}`);
        setWorkflow(response.data);
      } catch (err) {
        console.error('Failed to fetch workflow:', err);
        setError('Failed to load workflow data');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchWorkflow();
    }
  }, [id]);

  const handleSave = async (data) => {
    // Strip UI-only and DB-only fields before sending to API
    const payload = {
      ...data,
      items: data.items.map(item => ({
        id: item.id,
        type: item.type,
        description: item.description,
        stepOrder: item.stepOrder,
        required: item.required,
        identifierFormat: item.identifierFormat,
        audioRequired: item.audioRequired,
        allowedFileType: item.allowedFileType
      })),
    };

    await apiClient.put(`/workflows/${id}`, payload);
    toast.success('Workflow updated successfully');
    navigate(WORKFLOWS_ROUTE);
  };

  const handleCancel = () => {
    navigate(WORKFLOWS_ROUTE);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">Loading workflow data...</p>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="text-center py-24 glass-panel rounded-3xl border-dashed border-2 border-base-300">
        <div className="text-5xl mb-4 opacity-20">⚠️</div>
        <h3 className="text-xl font-black text-base-content mb-2 tracking-tight uppercase">Error Loading Workflow</h3>
        <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">
          {error || 'Workflow not found'}
        </p>
        <button
          onClick={() => navigate(WORKFLOWS_ROUTE)}
          className="mt-6 text-primary font-black uppercase tracking-widest text-[10px] hover:underline"
        >
          Back to Workflows
        </button>
      </div>
    );
  }

  return (
    <WorkflowBuilder
      workflow={workflow}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

export default WorkflowEdit;
